import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { FormField, Input, Label } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { DateInput } from '../ui/date-input';
import type { BtcApplication } from '../../types';

function toISODate(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

function fromISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

type FormData = Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>;

interface BtcApplicationFormProps {
  onSubmit: (data: FormData) => Promise<void>;
  onClose?: () => void;
  initialData?: BtcApplication | null;
}

export function BtcApplicationForm({ onSubmit, onClose, initialData }: BtcApplicationFormProps) {
  const [dateISO, setDateISO] = useState(toISODate(initialData?.date ?? new Date()));
  const [btcAmount, setBtcAmount] = useState(initialData?.btcAmount?.toString() ?? '');
  const [paidBrl, setPaidBrl] = useState(initialData?.paidBrl ?? 0);
  const [feeBtc, setFeeBtc] = useState(initialData?.feeBtc?.toString() ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDateISO(toISODate(initialData?.date ?? new Date()));
    setBtcAmount(initialData?.btcAmount?.toString() ?? '');
    setPaidBrl(initialData?.paidBrl ?? 0);
    setFeeBtc(initialData?.feeBtc?.toString() ?? '');
  }, [initialData]);

  const parsedBtc = parseFloat(btcAmount.replace(',', '.'));
  const parsedFee = parseFloat(feeBtc.replace(',', '.')) || 0;
  const isValid = !isNaN(parsedBtc) && parsedBtc > 0 && paidBrl > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      await onSubmit({
        date: fromISODate(dateISO),
        btcAmount: parsedBtc,
        paidBrl: paidBrl,
        feeBtc: parsedFee,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <FormField>
        <Label>Data</Label>
        <DateInput value={dateISO} onChange={setDateISO} />
      </FormField>

      <FormField>
        <Label>
          Quantidade BTC <span className="text-destructive">*</span>
        </Label>
        <Input
          value={btcAmount}
          onChange={(e) => setBtcAmount(e.target.value)}
          placeholder="Ex: 0.0044"
          inputMode="decimal"
        />
      </FormField>

      <FormField>
        <Label>
          Valor pago (R$) <span className="text-destructive">*</span>
        </Label>
        <CurrencyInput value={paidBrl} onChange={setPaidBrl} />
      </FormField>

      <FormField>
        <Label>Taxa (BTC)</Label>
        <Input
          value={feeBtc}
          onChange={(e) => setFeeBtc(e.target.value)}
          placeholder="Ex: 0.0000044"
          inputMode="decimal"
        />
      </FormField>

      <div className="flex gap-2">
        {onClose && (
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={!isValid || saving}>
          {initialData ? 'Salvar' : 'Adicionar'}
        </Button>
      </div>
    </form>
  );
}
